<template>
  <div class="main-desktop-header">
    <div class="main-desktop-header__aside" :class="asideClasses">
      <RouterLink
        :to="{ name: ROUTE_NAME.DASHBOARDS }"
        class="main-desktop-header__logo"
        :class="{ 'main-desktop-header__logo--shrinked': !isNavbarOpened }"
      >
        <AppLogo :full="isNavbarOpened" />
      </RouterLink>
      <span v-if="!disabled" class="main-desktop-header__toggler">
        <UIButtonPanel v-model="isNavbarOpened" variant="secondary" />
      </span>
    </div>
    <div
      v-if="repositoryName"
      class="main-desktop-header__repository"
      :class="{ 'main-desktop-header__repository--wide': !isNavbarOpened }"
    >
      <span class="main-desktop-header__repository-name">
        {{ repositoryName }}
      </span>
      <span v-if="repositoryOwner" class="main-desktop-header__repository-owner">{{ repositoryOwner }}</span>
    </div>
    <div v-if="isRefreshButtonVisible" class="main-desktop-header__refresh">
      <UIButton
        :leading="ArrowPathRoundedSquareIcon"
        label="Refresh analytics"
        variant="secondary"
        fill="light"
        @click="handleRefresh"
      />
    </div>
    <div v-if="isDisruptionMessageVisible" class="main-desktop-header__disruption-message">
      <AppDisruptionMessage  />
    </div>
    <div class="main-desktop-header__actions">
      <HeaderPowerMode />
      <HeaderNotifications />
      <HeaderSearch v-if="isSearchVisible" />
      <HeaderUser />
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, ComputedRef, inject } from 'vue'
import { useRoute } from 'vue-router'

import { OpenedFlag } from '@types'

import { NAVBAR_OPENED, ROUTE_NAME, IS_DISRUPTION_MESSAGE_VISIBLE, ADVISOR_MODE } from '@/const'

import { useHotKeys } from '@/plugins/hotKeys'

import useAnalyticsStore from '@/store/analytics'
import { useRepositoriesStore } from '@/store/repositories/index'

import { AppLogo, AppDisruptionMessage } from '@app'

import HeaderNotifications from './HeaderNotifications.vue'
import HeaderSearch from './HeaderSearch.vue'
import HeaderUser from './HeaderUser.vue'
import HeaderPowerMode from '../../components/HeaderPowerMode.vue'
import { ArrowPathRoundedSquareIcon } from '@heroicons/vue/24/outline'
import { UIButton, UIButtonPanel } from '@ui/buttons'

type Props = {
  disabled?: boolean
}

const props = defineProps<Props>()

const analyticsStore = useAnalyticsStore()
const repositoriesStore = useRepositoriesStore()

const route = useRoute()

const isDisruptionMessageVisible = inject<boolean>(IS_DISRUPTION_MESSAGE_VISIBLE, false)
const isNavbarOpened = inject<OpenedFlag>(NAVBAR_OPENED)
const isAdvisorMode = inject<ComputedRef<boolean>>(ADVISOR_MODE)

const repositoryName = computed(() => isAdvisorMode?.value && route.name?.toString().startsWith(ROUTE_NAME.ADVISOR) ? 'Advisor mode' : repositoriesStore.getCurrentName)
const repositoryOwner = computed(() => repositoriesStore.isOwner || isAdvisorMode?.value ? undefined : repositoriesStore.getCurrentOwner)

const isSearchVisible = computed(() => {
  return !props.disabled && !isAdvisorMode?.value
})

const asideClasses = computed(() => ({
  'main-desktop-header__aside--empty': props.disabled,
}))

const isRefreshButtonVisible = computed(() => {
  return (
    analyticsStore.isWaitingToRefresh &&
    route.name !== ROUTE_NAME.DASHBOARDS_DASHBOARD &&
    !route.name?.toString().startsWith('analytics-')
  )
})

const handleRefresh = () => {
  analyticsStore.markAsDeprecated()
}

useHotKeys('navbar', () => {
  if (!isNavbarOpened) return
  isNavbarOpened.value = !isNavbarOpened.value
})
</script>

<style scoped>
.main-desktop-header {
  @apply w-full h-10;
  @apply flex shrink-0 justify-between;
  @apply bg-primary;
  @apply z-20;

  &__aside {
    @apply flex shrink-0;
    @apply transition-all;

    .main-desktop--opened-navbar & {
      @apply w-56;
      @apply justify-between;
      @apply px-4;
      @apply bg-primary-dark;
    }
  }

  &__toggler {
    @apply h-full;
    @apply relative;
    @apply flex items-center justify-center;
    @apply ml-2.5 mr-2;
    @apply transition-all;

    :deep(.ui-button) {
      @apply bg-transparent hover:bg-primary-active;
      @apply border-indigo-600;
      @apply text-indigo-300 hover:text-indigo-100;
      @apply ring-primary-active ring-offset-primary;

      .main-desktop--opened-navbar & {
        @apply text-indigo-100 dark:text-indigo-400 dark:hover:text-indigo-300;
        @apply bg-primary;
        @apply ring-primary ring-offset-primary-dark;
      }
    }

    .main-desktop--opened-navbar & {
      @apply -mr-1.5 ml-0;
    }
  }

  &__logo {
    @apply flex items-center;
    @apply gap-1;
    @apply text-white;

    &--shrinked {
      @apply w-[4.2rem];
      @apply justify-center;
      @apply bg-primary-dark;
    }
  
  }

  &__repository {
    @apply h-full;
    @apply flex items-center gap-2;
    @apply text-white;
    @apply text-sm font-medium;
    @apply transition-all;
    @apply overflow-hidden;

    .main-desktop--opened-navbar & {
      @apply pl-4;
    }

    &--wide {
      @apply !pl-0;
      @apply -ml-0.5;
    }
  }

  &__repository-name {
    @apply truncate;
  }

  &__repository-owner {
    @apply text-xs font-normal;
    @apply text-indigo-400;
    @apply truncate;
  }

  &__refresh {
    @apply flex items-center;
    @apply ml-2;

    .ui-button {
      @apply px-2;
      @apply hover:bg-transparent;
      @apply hover:text-indigo-300;
      @apply !outline-none !ring-0;
    }
  }

  &__actions {
    @apply flex flex-auto items-center justify-end;
    @apply pr-6;
    @apply gap-x-2;
  }

  &__disruption-message {
    @apply absolute top-0.5 lg:top-2 left-1/2 -translate-x-1/2;
    @apply flex items-center justify-center;
    @apply flex-auto px-2;

    .ui-alert {
      @apply max-w-72 lg:max-w-full;
      @apply py-0.5 lg:py-1;
      @apply rounded;
    }

    :deep(.ui-alert__message) {
      @apply text-xs;
      @apply font-normal;
      @apply lg:truncate;
    }
  }
}
</style>

<style>
.main-desktop-header-button {
  @apply w-8;
  @apply aspect-square;
  @apply flex items-center justify-center;
  @apply rounded-full;
  @apply hover:bg-primary-active;
  @apply text-indigo-200;
  @apply cursor-pointer;
}

.main-desktop-header-icon {
  @apply w-5 aspect-square;
}
</style>
